import { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { FORMAT_LABELS, SITUATION_LABELS } from "@/lib/api";

export const EMPTY_PROFILE = {
  situation: "", headline: "", location: "", education: [], work_history: [], target_role: "",
  target_industry: "", skills: [], study_hours_per_week: 5, preferred_formats: [], portfolio_links: [], is_public: false,
};

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-slate-800">{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

export function SituationSection({ value, onChange }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2" role="radiogroup">
      {Object.entries(SITUATION_LABELS).map(([k, label]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)}
          data-testid={`situation-${k}`}
          className={`rounded-xl border-2 p-4 text-left text-sm font-semibold transition-colors ${value === k ? "border-[#222d99] bg-[#e8eaf6] text-[#222d99]" : "border-slate-200 text-slate-700 hover:border-slate-400"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function ListEditor({ items, onChange, fields, blank, addLabel, testid }) {
  const update = (i, key, v) => onChange(items.map((it, j) => (j === i ? { ...it, [key]: v } : it)));
  return (
    <div className="space-y-4" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="relative grid gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:grid-cols-2">
          {fields.map((f) => (
            <Field key={f.key} label={f.label}>
              {f.textarea
                ? <Textarea value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} maxLength={600} data-testid={`${testid}-${f.key}-${i}`} />
                : <Input value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} maxLength={120} data-testid={`${testid}-${f.key}-${i}`} />}
            </Field>
          ))}
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove"
            className="absolute right-2 top-2 rounded p-1 text-slate-400 hover:text-rose-600" data-testid={`${testid}-remove-${i}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...items, { ...blank }])} data-testid={`${testid}-add-btn`}>
        <Plus className="mr-1 h-4 w-4" /> {addLabel}
      </Button>
    </div>
  );
}

export const EducationSection = ({ items, onChange }) => (
  <ListEditor items={items} onChange={onChange} testid="education" addLabel="Add education"
    blank={{ institution: "", qualification: "", field: "", year: "" }}
    fields={[{ key: "institution", label: "School / institution" }, { key: "qualification", label: "Qualification" },
      { key: "field", label: "Field of study" }, { key: "year", label: "Year completed" }]} />
);

export const WorkSection = ({ items, onChange }) => (
  <ListEditor items={items} onChange={onChange} testid="work" addLabel="Add work experience"
    blank={{ company: "", title: "", start: "", end: "", summary: "" }}
    fields={[{ key: "company", label: "Company" }, { key: "title", label: "Job title" }, { key: "start", label: "Start (e.g. 2021)" },
      { key: "end", label: "End (or Present)" }, { key: "summary", label: "What you did", textarea: true }]} />
);

export function TargetSection({ p, set }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Target role"><Input value={p.target_role} onChange={(e) => set("target_role", e.target.value)} placeholder="e.g. Customer Support Associate" maxLength={100} data-testid="target-role-input" /></Field>
      <Field label="Target industry"><Input value={p.target_industry} onChange={(e) => set("target_industry", e.target.value)} placeholder="e.g. Fintech" maxLength={100} data-testid="target-industry-input" /></Field>
      <Field label="Headline (optional)"><Input value={p.headline} onChange={(e) => set("headline", e.target.value)} placeholder="Graduate ready for support roles" maxLength={140} data-testid="headline-input" /></Field>
      <Field label="Location (optional)"><Input value={p.location} onChange={(e) => set("location", e.target.value)} placeholder="Lagos, Nigeria" maxLength={80} data-testid="location-input" /></Field>
    </div>
  );
}

export function SkillsSection({ skills, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const s = draft.trim();
    if (s && !skills.some((x) => x.toLowerCase() === s.toLowerCase())) onChange([...skills, s]);
    setDraft("");
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={50} placeholder="Type a skill and press Enter"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="skill-input" />
        <Button type="button" variant="outline" onClick={add} data-testid="skill-add-btn">Add</Button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="skill-list">
        {skills.map((s) => (
          <span key={s} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1 text-sm">
            {s} <span className="text-[10px] uppercase text-slate-400">self-declared</span>
            <button type="button" onClick={() => onChange(skills.filter((x) => x !== s))} aria-label={`Remove ${s}`} data-testid={`skill-remove-${s}`}><X className="h-3.5 w-3.5 text-slate-400 hover:text-rose-600" /></button>
          </span>
        ))}
        {!skills.length && <p className="text-sm text-slate-500">No skills yet.</p>}
      </div>
    </div>
  );
}

export function StudySection({ p, set }) {
  const toggle = (f) => set("preferred_formats", p.preferred_formats.includes(f) ? p.preferred_formats.filter((x) => x !== f) : [...p.preferred_formats, f]);
  return (
    <div className="space-y-8">
      <Field label={`Study time per week: ${p.study_hours_per_week} hours`}>
        <Slider min={1} max={40} step={1} value={[p.study_hours_per_week]} onValueChange={([v]) => set("study_hours_per_week", v)} data-testid="study-hours-slider" />
      </Field>
      <Field label="Preferred learning formats">
        <div className="flex flex-wrap gap-2">
          {Object.entries(FORMAT_LABELS).map(([k, label]) => (
            <button key={k} type="button" onClick={() => toggle(k)} data-testid={`format-toggle-${k}`} aria-pressed={p.preferred_formats.includes(k)}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${p.preferred_formats.includes(k) ? "border-[#222d99] bg-[#222d99] text-white" : "border-slate-300 text-slate-700 hover:border-slate-500"}`}>
              {label}
            </button>
          ))}
        </div>
      </Field>
    </div>
  );
}

export function PortfolioSection({ links, onChange }) {
  return (
    <div className="space-y-2">
      {links.map((l, i) => (
        <div key={i} className="flex gap-2">
          <Input value={l} onChange={(e) => onChange(links.map((x, j) => (j === i ? e.target.value : x)))} placeholder="https://" data-testid={`portfolio-link-${i}`} />
          <Button type="button" variant="ghost" onClick={() => onChange(links.filter((_, j) => j !== i))} data-testid={`portfolio-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...links, ""])} disabled={links.length >= 10} data-testid="portfolio-add-btn"><Plus className="mr-1 h-4 w-4" /> Add link</Button>
    </div>
  );
}

export const cleanProfile = (p) => ({
  ...p,
  education: p.education.filter((e) => e.institution?.trim()),
  work_history: p.work_history.filter((w) => w.company?.trim()),
  portfolio_links: p.portfolio_links.filter((l) => l.trim()),
});
